/* The agency desk's modules, grouped the way the console's rail is grouped.
 *
 * Same shape as apps/admin/src/nav.ts so the two apps read alike: a key that is
 * the route, a label, the one line under the page title, and an icon from the
 * shared set. `count` names which of /agency/profile's counts the rail shows
 * beside the item — the number that needs someone's attention.
 *
 * `manage` marks a module only OWNER and ADMIN may change. It is shown to every
 * role (an accountant may read what the agency offers) and its write controls
 * are hidden; the server refuses them anyway (ROLE_NOT_PERMITTED). Hiding a
 * button is not a permission. */

import type { IconName } from './ui';
import type { Profile } from './data/agency';

export type ModuleKey = 'overview' | 'requests' | 'assign' | 'live' | 'team' | 'coverage' | 'prices' | 'documents' | 'earnings' | 'profile';

export interface ModuleInfo {
  key: ModuleKey;
  label: string;
  sub: string;
  icon: IconName;
  count?: keyof Profile['counts'];
}

export const MODULES: Record<ModuleKey, ModuleInfo> = {
  overview: { key: 'overview', label: 'Overview', sub: 'What needs you now, and whether requests can reach you', icon: 'board' },
  requests: {
    key: 'requests',
    label: 'Requests',
    sub: 'Families near you who need care. Answer before the timer runs out',
    icon: 'bell',
    count: 'open_offers',
  },
  assign: {
    key: 'assign',
    label: 'Assign staff',
    sub: 'Jobs you accepted — choose who goes',
    icon: 'dispatchList',
    count: 'to_staff',
  },
  live: { key: 'live', label: 'Live jobs', sub: 'Your people at work right now', icon: 'route', count: 'live_jobs' },
  team: {
    key: 'team',
    label: 'Team',
    sub: 'Your nurses, doctors and drivers, and people asking to join',
    icon: 'people',
    count: 'join_requests',
  },
  coverage: { key: 'coverage', label: 'Services & area', sub: 'What you offer, and where', icon: 'map' },
  prices: {
    key: 'prices',
    label: 'Prices',
    sub: 'What you charge for each service. Families pick by price; a type with no price is not shown to them',
    icon: 'tag',
  },
  documents: {
    key: 'documents',
    label: 'Documents & approval',
    sub: 'The papers the platform checks before it sends you work',
    icon: 'shieldCheck',
  },
  earnings: { key: 'earnings', label: 'Earnings', sub: 'What the platform owes you, and what it has paid', icon: 'banknote' },
  // Reached from the owner menu, not the rail (not listed in NAV). The page
  // head still needs its label, so it lives in MODULES.
  profile: { key: 'profile', label: 'Profile', sub: 'Your agency details, and your own name', icon: 'sliders' },
};

export const NAV: { group: string; items: ModuleKey[] }[] = [
  { group: 'Work', items: ['overview', 'requests', 'assign', 'live'] },
  { group: 'People', items: ['team'] },
  { group: 'Setup', items: ['coverage', 'prices', 'documents'] },
  { group: 'Money', items: ['earnings'] },
];

/** OWNER and ADMIN manage the agency; DISPATCHER takes requests and assigns;
 *  ACCOUNTANT reads. */
export function canManage(role: string): boolean {
  return role === 'OWNER' || role === 'ADMIN';
}

export function canDispatch(role: string): boolean {
  return role === 'OWNER' || role === 'ADMIN' || role === 'DISPATCHER';
}

export const ROLE_WORD: Record<string, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  DISPATCHER: 'Dispatcher',
  ACCOUNTANT: 'Accountant',
};

export const AGENCY_STATUS_WORD: Record<string, { label: string; tone: 'live' | 'partial' | 'demo' | 'neutral' }> = {
  ACTIVE: { label: 'Approved', tone: 'live' },
  PENDING_REVIEW: { label: 'Waiting for approval', tone: 'partial' },
  DRAFT: { label: 'Not submitted', tone: 'partial' },
  SUSPENDED: { label: 'Suspended', tone: 'demo' },
  REJECTED: { label: 'Not approved', tone: 'demo' },
  TERMINATED: { label: 'Closed', tone: 'demo' },
};
